import { useEffect, useState } from 'react'
import { Button, Group, Modal, MultiSelect, Stack, Text, TextInput, Textarea } from '@mantine/core'
import { useForm } from '@mantine/form'
import { showNotification } from '@mantine/notifications'
import { formatHumanName, normalizeErrorString } from '@medplum/core'
import type { Patient, Practitioner } from '@medplum/fhirtypes'
import { useMedplum, useMedplumProfile } from '@medplum/react'
import { IconCircleCheck, IconCircleOff, IconPlus, IconX } from '@tabler/icons-react'
import {
  CANCEL_BTN,
  FIELD_CLASSES,
  FIELD_LABEL,
  GHOST_BTN,
  MODAL_CONTENT,
  MODAL_FOOTER,
  SUBMIT_BTN,
} from '@/components/ui/formModal.tokens'
import { useMiddlewareClient } from '@/hooks'
import { logError } from '@/utils/errorUtils'
import {
  ACCESS_POLICY_BADGE_LABELS,
  getOrganizationPractitioners,
  type MessageThreadSummary,
  type PractitionerThreadOption,
} from '../service'
import { PatientSelect } from '@/pages/CallCenterHome/components/PatientSelect/PatientSelect'
import { THREAD_MAX_RECIPIENTS } from '@/model/middleware-types'
import { formatNameList } from '../presentation'
import { PRIMARY_BUTTON_PROPS, labelToggleClass } from '../tokens'

type FormValues = {
  subject: string
  recipientIds: string[]
  initialMessage: string
}

type CreateThreadBtnProps = {
  /**
   * Receives the created thread so the caller can open it straight away —
   * `POST /threads` already returns the same summary shape the list uses.
   */
  onCreated?: (created?: MessageThreadSummary) => void
  preselectedPatient?: Patient | null
  /** Coral Schedule-v2 styling for the redesigned Messages page. */
  primary?: boolean
  label?: string
}

function getPractitionerLabel(practitioner: Practitioner): string {
  const fullName = formatHumanName(practitioner.name?.[0])
  return fullName || practitioner.id || 'Unnamed practitioner'
}

export const CreateThreadBtn = ({
  onCreated,
  preselectedPatient = null,
  primary = false,
  label = 'Create Thread',
}: CreateThreadBtnProps) => {
  const medplum = useMedplum()
  const middleware = useMiddlewareClient()
  const profile = useMedplumProfile()
  const practitionerProfile = profile?.resourceType === 'Practitioner' ? profile : undefined

  const [opened, setOpened] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [loadingPractitioners, setLoadingPractitioners] = useState(false)
  const [practitioners, setPractitioners] = useState<PractitionerThreadOption[]>([])
  const [isMarkedAsUrgent, setIsMarkedAsUrgent] = useState(false)
  const [isMarkedAsFollowUp, setIsMarkedAsFollowUp] = useState(false)
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(preselectedPatient)

  const form = useForm<FormValues>({
    initialValues: {
      subject: '',
      recipientIds: [],
      initialMessage: '',
    },
    validate: {
      subject: (value) =>
        value.trim().length < 3 ? 'Subject must be at least 3 characters.' : null,
      recipientIds: (value) => {
        if (value.length === 0) {
          return 'Select at least one recipient practitioner.'
        }

        return value.length > THREAD_MAX_RECIPIENTS
          ? `Select at most ${THREAD_MAX_RECIPIENTS} recipients.`
          : null
      },
      initialMessage: (value) =>
        value.trim().length < 3 ? 'Initial message must be at least 3 characters.' : null,
    },
  })
  const handlePatientChange = (patient: Patient | null) => {
    setSelectedPatient(patient)
  }

  useEffect(() => {
    setSelectedPatient(preselectedPatient)
  }, [preselectedPatient])

  useEffect(() => {
    if (!opened || !practitionerProfile?.id) {
      return
    }

    const practitionerId = practitionerProfile.id
    let cancelled = false

    async function loadPractitioners(): Promise<void> {
      setLoadingPractitioners(true)
      try {
        const result = await getOrganizationPractitioners(medplum, practitionerId)
        if (!cancelled) {
          setPractitioners(result)
        }
      } catch (err) {
        if (!cancelled) {
          showNotification({
            color: 'red',
            icon: <IconCircleOff size={16} />,
            title: 'Unable to load practitioners',
            message: normalizeErrorString(err),
          })
        }
      } finally {
        if (!cancelled) {
          setLoadingPractitioners(false)
        }
      }
    }

    loadPractitioners().catch((err) => logError('messages', 'Failed to load practitioners', err))

    return () => {
      cancelled = true
    }
  }, [opened, medplum, practitionerProfile?.id])

  const closeModal = (): void => {
    form.reset()
    setIsMarkedAsUrgent(false)
    setIsMarkedAsFollowUp(false)
    if (preselectedPatient) {
      // If there is a preselected patient, reset to that patient when closing the modal instead of clearing the selection entirely.
      setSelectedPatient(preselectedPatient)
    } else {
      setSelectedPatient(null)
    }
    setOpened(false)
  }

  const handleSubmit = form.onSubmit(async (values) => {
    if (!practitionerProfile?.id) {
      showNotification({
        color: 'red',
        icon: <IconCircleOff size={16} />,
        title: 'Unable to create thread',
        message: 'Current Medplum profile is not a practitioner.',
      })
      return
    }

    const recipientOptions = values.recipientIds
      .map((id) => practitioners.find((item) => item.practitioner.id === id))
      .filter((option): option is PractitionerThreadOption => Boolean(option?.practitioner.id))
    // `Practitioner.id` is optional in FHIR, so the ids are narrowed above
    // rather than sending `undefined` as a recipient — the endpoint requires it.
    const recipientPractitionerIds = recipientOptions.map((option) => option.practitioner.id!)
    if (recipientPractitionerIds.length !== values.recipientIds.length) {
      form.setFieldError('recipientIds', 'Select valid practitioners.')
      return
    }

    setSubmitting(true)

    try {
      const createThreadPayload = {
        recipientPractitionerIds,
        subject: values.subject.trim(),
        initialMessage: values.initialMessage.trim(),
        isMarkedAsUrgent,
        isMarkedAsFollowUp,
        subjectPatientId: selectedPatient?.id,
      }
      const created = (await middleware.post('/threads', createThreadPayload)) as
        | MessageThreadSummary
        | undefined

      showNotification({
        color: 'green',
        icon: <IconCircleCheck size={16} />,
        title: 'Thread created',
        message: `New conversation started with ${formatNameList(
          recipientOptions.map((option) => getPractitionerLabel(option.practitioner)),
        )}.`,
      })

      onCreated?.(created)
      closeModal()
    } catch (err) {
      showNotification({
        color: 'red',
        icon: <IconCircleOff size={16} />,
        title: 'Unable to create thread',
        message: normalizeErrorString(err),
      })
    } finally {
      setSubmitting(false)
    }
  })

  const practitionerOptions = practitioners.map((item) => ({
    value: item.practitioner.id as string,
    label: getPractitionerLabel(item.practitioner),
    accessPolicyName: item.accessPolicyName,
  }))

  return (
    <>
      <Button
        {...(primary ? PRIMARY_BUTTON_PROPS : { className: 'bg-blue-500 text-white' as const })}
        leftSection={<IconPlus size={16} />}
        onClick={() => setOpened(true)}
      >
        {label}
      </Button>

      <Modal
        opened={opened}
        onClose={closeModal}
        centered
        withCloseButton={false}
        classNames={{ content: MODAL_CONTENT, body: '!p-0' }}
        radius={18}
        size="560px"
      >
        <form onSubmit={handleSubmit} className="flex max-h-[86vh] flex-col">
          {/* Header — pinned above the scrolling body, matching the app's other form modals. */}
          <div className="flex items-start gap-2.5 px-6 pt-5 pb-4">
            <div className="flex-1">
              <h2 className="m-0 text-[17px] font-extrabold tracking-tight text-[#1F2733]">
                New practitioner thread
              </h2>
              <p className="m-0 text-[12.5px] text-[#8A929C]">
                Start a secure conversation with a care-team member.
              </p>
            </div>
            <button
              type="button"
              title="Close"
              aria-label="Close"
              onClick={closeModal}
              className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-[7px] text-[#8A929C] hover:bg-[#F2F4F6] hover:text-[#1F2733]"
            >
              <IconX size={17} stroke={2.1} />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto border-t border-[#EEF0F2] px-6 py-5">
            <Stack gap="md">
              <TextInput
                label="From"
                classNames={FIELD_CLASSES}
                value={
                  practitionerProfile
                    ? getPractitionerLabel(practitionerProfile)
                    : 'Unknown practitioner'
                }
                readOnly
              />
              <MultiSelect
                label="To"
                placeholder={
                  form.values.recipientIds.length > 0 ? undefined : 'Select practitioners'
                }
                description={`Add up to ${THREAD_MAX_RECIPIENTS} care-team members.`}
                searchable
                clearable
                hidePickedOptions
                maxValues={THREAD_MAX_RECIPIENTS}
                classNames={FIELD_CLASSES}
                data={practitionerOptions}
                renderOption={({ option }) => (
                  <Group justify="space-between" wrap="nowrap" w="100%">
                    <Text size="sm">{option.label}</Text>
                    <span className="behuman-v2-mulish rounded bg-[#F1F2F4] px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-[#6B7280] uppercase">
                      {
                        ACCESS_POLICY_BADGE_LABELS[
                          (
                            option as typeof option & {
                              accessPolicyName: PractitionerThreadOption['accessPolicyName']
                            }
                          ).accessPolicyName
                        ]
                      }
                    </span>
                  </Group>
                )}
                disabled={loadingPractitioners}
                nothingFoundMessage={
                  loadingPractitioners ? 'Loading practitioners...' : 'No practitioners found'
                }
                {...form.getInputProps('recipientIds')}
              />

              <TextInput
                label="Thread subject"
                placeholder="Enter thread subject"
                classNames={FIELD_CLASSES}
                {...form.getInputProps('subject')}
              />

              <div>
                <div className={FIELD_LABEL}>Related patient</div>
                {selectedPatient ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#1F2733]">
                      {formatHumanName(selectedPatient.name?.[0])}
                    </span>
                    <button
                      type="button"
                      className={GHOST_BTN}
                      onClick={() => setSelectedPatient(null)}
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <PatientSelect
                    placeholder="Select patient"
                    setSelectedPatient={handlePatientChange}
                    hideAddPatientButton
                  />
                )}
              </div>

              <Group gap="sm">
                <button
                  type="button"
                  aria-pressed={isMarkedAsUrgent}
                  className={labelToggleClass(isMarkedAsUrgent, 'urgent')}
                  onClick={() => setIsMarkedAsUrgent((current) => !current)}
                >
                  {isMarkedAsUrgent ? 'Unmark as urgent' : 'Mark as urgent'}
                </button>
                <button
                  type="button"
                  aria-pressed={isMarkedAsFollowUp}
                  className={labelToggleClass(isMarkedAsFollowUp, 'followUp')}
                  onClick={() => setIsMarkedAsFollowUp((current) => !current)}
                >
                  {isMarkedAsFollowUp ? 'Clear follow up' : 'Require follow up'}
                </button>
              </Group>

              <Textarea
                label="Initial message"
                placeholder="Write the first message in this thread"
                minRows={4}
                autosize
                classNames={{ root: 'w-full', label: FIELD_LABEL }}
                {...form.getInputProps('initialMessage')}
              />
            </Stack>
          </div>

          <div className={MODAL_FOOTER}>
            <button type="button" className={`${CANCEL_BTN} ml-auto`} onClick={closeModal}>
              Cancel
            </button>
            <button type="submit" className={SUBMIT_BTN} disabled={submitting}>
              {submitting ? 'Creating…' : 'Create thread'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  )
}
